/**
 * TODO - preamble
 */
import React, { useEffect } from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';

import {
    handleEmailOnKeyDown,
    submitForm,
    updateFieldValidationErrorsByFieldId,
    updateViewModelOrganization,
    getAvailableMilitaryStatuses,
    getStatusLabel,
    CountryComponentWrapper,
    HowDoesVerifyingWorkComponent as HowDoesVerifyingWork,
    LogoComponent,
    PhoneNumberComponent as PhoneNumber,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    EmailComponent as Email,
    BirthDateComponent as BirthDate,
    MilitaryStatusComponent as MilitaryStatus,
    BranchOfServiceComponent as BranchOfService,
    FormFooterComponent as FormFooter,
    MarketConsentWrapper,
    RewardsRemainingComponent,
    updateMilitaryViewModel,
    VerificationService,
    FormSelectChoice,
    Organization,
    ActiveMilitaryPersonalInfoViewModel,
    ActiveMilitaryPersonalInfoResponse,
    isFormFilled,
    hasFailedInstantMatch,
    setRef,
    logger,
    resetRefs,
    VerificationStepsEnum,
    MilitaryStatusDefaultMessagesEnum,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    ErrorId,
    FieldId,
} from '@sheerid/jslib';

import { MarinerNumber } from '../FormFields/MarinerNumber';
import { getQueryParamsByName } from '../../lib/utils/browser';

interface StepActiveMilitaryPersonalInfoComponentProps {
  intl: any;
  verificationService: VerificationService;
}

export const StepActiveMilitaryPersonalInfo = ({
    intl,
    verificationService,
}: StepActiveMilitaryPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as ActiveMilitaryPersonalInfoViewModel;
    const previousViewModel = verificationService.previousViewModel as ActiveMilitaryPersonalInfoViewModel;
    const { fieldValidationErrors }: {fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors} = verificationService;
    const verificationResponse = verificationService.verificationResponse as ActiveMilitaryPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
    const { status } = verificationService.viewModel as ActiveMilitaryPersonalInfoViewModel;

    const availableStatuses = getAvailableMilitaryStatuses(verificationService, intl);

    const updateActiveMilitaryViewModel = (
        key: keyof ActiveMilitaryPersonalInfoViewModel,
        value: any,
    ) => {
        verificationService.updateViewModel(
            updateMilitaryViewModel(key, value, verificationService, availableStatuses),
        );
    };

    const submitStep = () => {
        const nextState = produce(viewModel, () => {});
        const response = (
            verificationService.previousVerificationResponse?.currentStep === VerificationStepsEnum.collectMilitaryStatus
            && verificationService.verificationResponse.currentStep !== VerificationStepsEnum.collectMilitaryStatus
        )
            ? verificationService.previousVerificationResponse
            : verificationService.verificationResponse;

        logger.info('StepMilitaryStatusComponent submitting form');
        verificationService.submitStep(
            VerificationStepsEnum.collectMilitaryStatus,
            nextState,
            response,
        );
    };

    if (
        (viewModel.status && previousViewModel && previousViewModel.status !== viewModel.status)
    || (viewModel.status
      && verificationService.verificationResponse.currentStep
        === VerificationStepsEnum.collectMilitaryStatus)
    ) {
        submitStep();
    }

    useEffect(() => {
        let marinerNumber: string = verificationService.viewModel.metadata?.captainsCircleNumber as string || getQueryParamsByName('marinerNumber') || '';
        if (verificationService?.previousVerificationResponse
            && verificationService?.previousVerificationResponse?.currentStep === 'docUpload') {
            // reset the marinerNumber if the user came from doc upload via max doc review failure
            marinerNumber = '';
        }
        updateActiveMilitaryViewModel('metadata', { ...verificationService.viewModel.metadata, captainsCircleNumber: marinerNumber });
    }, []);

    // Refs should be reset to ensure the field order isn't affected by the previous step
    resetRefs();

    return (
        <div id="sid-step-active-military-personal-info" className="sid-l-container">
            {failedInstantMatch ? (
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.tryAgain.title"
                            defaultMessage="Check Your Name & Birthdate"
                        />
                    </div>

                    <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                        <FormattedHTMLMessage
                            id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="
                                Enter your first and last name on the form as it appears on your military document(s).
                                Keep in mind that may be your given name."
                        />
                    </div>
                </div>
            ) : (
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.title"
                            defaultMessage="Unlock this Military-Only Offer"
                            tagName="h1"
                        />
                    </div>

                    <div className="sid-header__subtitle sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.subtitle"
                            defaultMessage="Verify you're a member of the armed forces."
                        />
                    </div>
                    <div className="sid-header__how-verifying-works sid-l-horz-center">
                        <HowDoesVerifyingWork verificationService={verificationService} />
                        <RewardsRemainingComponent verificationService={verificationService} />
                    </div>
                </div>
            )}

            <CountryComponentWrapper verificationService={verificationService} viewModel={viewModel} />

            <MarinerNumber
                value={viewModel.metadata?.captainsCircleNumber}
                isErrored={!!fieldValidationErrors.captainsCircleNumber}
                errorId={fieldValidationErrors.captainsCircleNumber as ErrorId}
                onChange={(newValue: string) => {
                    updateActiveMilitaryViewModel('metadata', { ...verificationService.viewModel.metadata, captainsCircleNumber: newValue });
                    updateFieldValidationErrorsByFieldId('captainsCircleNumber' as FieldId, newValue, verificationService);
                }}
            />

            {availableStatuses ? (
                <MilitaryStatus
                    value={{
                        value: viewModel.status,
                        label: getStatusLabel(intl, viewModel.status, MilitaryStatusDefaultMessagesEnum),
                    }}
                    isErrored={!!fieldValidationErrors.status}
                    options={availableStatuses}
                    onChange={(status: FormSelectChoice) => {
                        updateActiveMilitaryViewModel('status', status ? (status.value as string) : '');
                        updateFieldValidationErrorsByFieldId(
                            'status',
                            status ? (status.value as string) : '',
                            verificationService,
                        );
                    }}
                />
            ) : null}

            {status === 'MILITARY_FAMILY' ? (
                <div className="sid-field-notice" data-testid="family-note">
                    <FormattedHTMLMessage
                        id="militaryFamilyCardNote"
                        defaultMessage="Note: this offer is only available to military family members with a Dependent ID card."
                    />
                </div>
            ) : null}

            <BranchOfService
                isErrored={!!fieldValidationErrors.organization}
                onChange={(choice: Organization) => {
                    updateViewModelOrganization(choice, verificationService);
                    updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                }}
                value={{
                    value: (verificationService.viewModel as ActiveMilitaryPersonalInfoViewModel).organization
                        .id,
                    label: (verificationService.viewModel as ActiveMilitaryPersonalInfoViewModel).organization
                        .name,
                }}
                verificationService={verificationService}
                organizations={verificationService.orgList}
            />

            <div className="sid-names">
                <FirstName
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={(newValue: string) => {
                        updateActiveMilitaryViewModel('firstName', newValue);
                        updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                    }}
                />

                <LastName
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={(newValue: string) => {
                        updateActiveMilitaryViewModel('lastName', newValue);
                        updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                    }}
                />
            </div>

            <BirthDate
                isErrored={!!fieldValidationErrors.birthDate}
                errorId={fieldValidationErrors.birthDate}
                onChange={(newValue: string) => {
                    updateActiveMilitaryViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={(
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Personal email address is recommended"
                    />
                )}
                onChange={(newValue: string) => {
                    updateActiveMilitaryViewModel('email', newValue);
                    updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                }}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />
            {verificationService.programTheme.isSmsNotifierConfigured
      || verificationService.programTheme.smsLoopEnabled ? (
                    <PhoneNumber
                        isRequired={!!verificationService.programTheme.smsLoopEnabled}
                        value={viewModel.phoneNumber}
                        isErrored={!!fieldValidationErrors.phoneNumber}
                        onChange={(newValue: string) => {
                            updateActiveMilitaryViewModel('phoneNumber', newValue);
                            updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                        }}
                        selectedCountryCode={viewModel.countryChoice && viewModel.countryChoice.value}
                    />
                ) : null}
            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateActiveMilitaryViewModel('metadata', {
                        ...verificationService.viewModel.metadata,
                        marketConsentValue: newValue,
                    });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(
                            viewModel,
                            verificationService,
                            VerificationStepsEnum.collectActiveMilitaryPersonalInfo,
                        )}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${
                            !isFormFilled(viewModel, verificationService.formValidationOptions)
                                ? 'sid-btn--disabled-like'
                                : ''
                        }`}
                        aria-labelledby="verify-status-text"
                        ref={(button) => setRef('submitButton', button)}
                    >
                        <span id="verify-status-text">
                            <FormattedHTMLMessage
                                id="verifyMyMilitaryStatus"
                                defaultMessage="Verify My Military Status"
                            />
                        </span>
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService} />
        </div>
    );
};

export const StepActiveMilitaryPersonalInfoComponent = injectIntl(StepActiveMilitaryPersonalInfo);
